// skillgenpro-frontend/src/error-framework/errorMapper.js
//
// normalizeError() turns whatever axios/fetch/a component throws into the
// same canonical shape the backend's errorResponse.js emits, so
// ErrorContext, recoveryEngine, and ErrorExperience never have to branch on
// "is this an axios error or a fetch error or a thrown Error?" — they only
// ever see one shape.
//
// Framework-agnostic on purpose: no React import here. Both
// axiosInterceptor.js (axios) and fetchWrapper.js (raw fetch) call this.

import { getRegistryEntry } from './errorRegistry';

/**
 * @typedef {Object} NormalizedError
 * @property {string} errorId
 * @property {string} correlationId
 * @property {string} category
 * @property {string} code
 * @property {string} userMessage
 * @property {boolean} retryable
 * @property {boolean} autoRetry
 * @property {'low'|'medium'|'high'|'critical'} severity
 * @property {Object} context
 * @property {string} timestamp
 * @property {*} [raw] - the original error, kept for logging only, never rendered
 */

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * If the backend already sent a canonical envelope (has errorId + category +
 * code), trust it — it already carries the right contextual message.
 */
function extractBackendEnvelope(responseData) {
  if (!isPlainObject(responseData)) return null;
  if (!responseData.errorId || !responseData.category || !responseData.code) return null;
  return {
    errorId: responseData.errorId,
    correlationId: responseData.correlationId || responseData.errorId,
    category: responseData.category,
    code: responseData.code,
    userMessage: responseData.error || responseData.userMessage,
    retryable: responseData.retryable !== false,
    autoRetry: responseData.autoRetry === true,
    severity: responseData.severity || 'medium',
    context: responseData.context || {},
    timestamp: responseData.timestamp || new Date().toISOString(),
  };
}

/**
 * Classifies a raw axios/fetch-shaped error when the backend didn't (or
 * couldn't — e.g. a cold-start crash, a network failure before any request
 * reached the server) send a canonical envelope.
 */
function classifyRawError(error) {
  // No response at all — network-layer failure.
  if (!error || (error.isAxiosError && !error.response) || (error.name === 'TypeError' && !error.status)) {
    if (typeof navigator !== 'undefined' && navigator && navigator.onLine === false) {
      return { code: 'NETWORK_OFFLINE', category: 'NETWORK' };
    }
    if (error && (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || ''))) {
      return { code: 'NETWORK_TIMEOUT', category: 'NETWORK' };
    }
    return { code: 'NETWORK_UNKNOWN', category: 'NETWORK' };
  }

  var status = (error.response && error.response.status) || error.status;

  if (status === 401) return { code: 'SESSION_EXPIRED', category: 'AUTHENTICATION' };
  if (status === 403) return { code: 'PERMISSION_DENIED', category: 'AUTHORIZATION' };
  if (status === 429) return { code: 'AI_QUOTA_EXCEEDED', category: 'AI' };
  if (status === 503) return { code: 'AI_QUOTA_EXCEEDED', category: 'AI' };
  if (status === 504 || status === 408) return { code: 'NETWORK_TIMEOUT', category: 'NETWORK' };

  return { code: 'UNKNOWN_ERROR', category: 'UNKNOWN' };
}

/**
 * The main entry point. Always returns a NormalizedError — never throws,
 * never returns undefined, so callers can rely on it unconditionally.
 *
 * @param {*} error - whatever was thrown/rejected (axios error, fetch
 *   Response-derived error, plain Error, or already-normalized error)
 * @param {Object} [context] - feature/page/role info from contextResolver,
 *   merged on top of anything the backend already sent
 */
export function normalizeError(error, context) {
  context = context || {};

  // Already normalized (e.g. re-thrown by fetchWrapper) — just merge context.
  if (error && error.errorId && error.category && error.code) {
    return { ...error, context: { ...(error.context || {}), ...context } };
  }

  var responseData = error && error.response && error.response.data;
  var fromBackend = extractBackendEnvelope(responseData);
  if (fromBackend) {
    return { ...fromBackend, context: { ...fromBackend.context, ...context }, raw: error };
  }

  var classified = classifyRawError(error);
  var entry = getRegistryEntry(classified.code, classified.category);

  return {
    errorId: 'client_' + Math.random().toString(36).slice(2, 10),
    correlationId: 'client_' + Date.now().toString(36),
    category: classified.category,
    code: classified.code,
    userMessage: entry.description(context),
    retryable: entry.retryable,
    autoRetry: entry.autoRetry,
    severity: entry.severity,
    context,
    timestamp: new Date().toISOString(),
    raw: error,
  };
}
